<script setup lang="ts">

import { T } from '@tolgee/vue'
import { Token } from '@/enums/token'
import { usePlayer } from '@/composables/game/player'
import { getTokenAssetUrl, tokenSlugMap } from '@/helpers/game'
import CheckCirclePinkBorder from '@/components/icons/CheckCirclePinkBorder.vue'
import api from '@/services/api'
import { computed, ref } from 'vue'
import { RouteName } from '@/routes'
import router from '@/router'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import ArrowRight from '@/components/icons/ArrowRight.vue'

const { player, chosenToken, updatePlayer } = usePlayer()
const { startCoinAnimation } = useFlyingCoins()

const loadingToken = ref<null | string>(null)
const isLoading = computed(() => loadingToken.value !== null)

async function choseToken(token: string) {
  loadingToken.value = token
  try {
    const playerTokenBeforeUpdate = player.value.token
    const { playerStats } = await api.setToken(token)
    if (!playerTokenBeforeUpdate) {
      startCoinAnimation()
    }
    updatePlayer(playerStats)
    await router.push({ name: !playerTokenBeforeUpdate ? RouteName.EARN : RouteName.HOME })
  } finally {
    setTimeout(() => loadingToken.value = null, 250)
  }
}
</script>

<template>
  <div class="flex flex-col w-full items-center pt-6 px-4 ">
    <img
      :src="CoinIcon"
      class="h-[120px] text-white bounce-down-fast"
      alt="Coin Icon"
    >
    <div
      class="mt-6 text-[32px] leading-[32px] font-bold uppercase text-light-white bounce-in-right"
    >
      <T
        key-name="tokens.choose_token"
        default-value="Choose Token"
      />
    </div>
    <div
      class="mt-10 w-full flex flex-col gap-y-3"
      :class="{'pointer-events-none': isLoading}"
    >
      <div
        v-for="token in Object.values(Token)"
        :key="token"
        class="h-[72px] p-2 pl-3 pr-4 rounded-xl flex items-center gap-x-3 min-w-0 bg-dark-bottom-layer border"
        :class="chosenToken === token ? 'border-teal-medium pointer-events-none' : 'border-transparent cursor-pointer'"
        @click="choseToken(token)"
      >
        <div
          class="w-12 h-12 min-w-[48px] flex items-center justify-center"
        >
          <img
            :src="getTokenAssetUrl(tokenSlugMap[token])"
            class="h-full w-auto object-cover scale-in"
          >
        </div>
        <div class="text-base font-semibold -tracking-[0.32px]">
          {{ token }}
        </div>
        <div class="ml-auto w-6 h-6 min-w-[24px] flex items-center justify-center">
          <SpinnerIcon
            v-if="loadingToken === token"
            class="text-white w-5 min-w-[20px]"
          />
          <CheckCirclePinkBorder
            v-else-if="chosenToken === token"
            class="w-full h-full"
          />
          <ArrowRight
            v-else
            class="text-white w-full h-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>